import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import GraphComponent from '../copilotComponents/GraphComponent';
import MentionAutocomplete from '../genbi/MentionAutocomplete';
import { imageSourceFromTransfer } from '../genbi/responseImageUpload';
import { copySelectedPills, pastedPillsHTML, rangeIsSinglePill, selectClickedPill } from '../genbi/citationClipboard';

// createRoot (React 18) with fallback to ReactDOM.render (React 17)
const _createRoot = (container) => {
  try {
    // eslint-disable-next-line
    const { createRoot } = require('react-dom/client');
    return createRoot(container);
  } catch (_) {
    return {
      render: (element) => ReactDOM.render(element, container),
      unmount: () => ReactDOM.unmountComponentAtNode(container),
    };
  }
};

// These graph sub-roots live inside DOM that the parent root owns via
// dangerouslySetInnerHTML. Calling root.unmount() while the parent is committing
// re-enters React (unmount runs flushSync internally) and corrupts the parent's
// DOM bookkeeping — that is the `removeChild` NotFoundError / white screen.
// Running the teardown in a later task lets the parent finish committing first.
// By then the container is detached, so the unmount is a harmless no-op teardown.
// Only use this where nothing re-mounts into the same container in this pass.
const deferUnmount = (roots) => {
  const list = (Array.isArray(roots) ? roots : [roots]).filter(Boolean);
  if (list.length === 0) return;
  setTimeout(() => {
    list.forEach(root => {
      try { root.unmount(); } catch (_) { /* container already gone */ }
    });
  }, 0);
};

const PORTAL_RE = /<div[^>]+class="[^"]*genbi-inplace-graph-portal[^"]*"[^>]+data-ipg-index="(\d+)"[^>]*>[\s\S]*?<\/div>/g;
const GRAPH_MARKER_RE = /\[\[InPlace_Graph(\d+)\]\]/g;
const IMAGE_MARKER_RE = /\[\[InPlace_Image(\d+)\]\]/g;
const CITATION_BADGE_SELECTOR = '.ev-message-card6, .ev-message-card7';
// A cell holding one of these has a mounted React root in it, so its children are
// not ours to move around.
const FIGURE_CELL_SELECTOR = '.genbi-inplace-figure, [data-ipg-root], .genbi-inplace-graph-portal';
// What "Mark table as changed" colours with when the table carries no mark of its
// own and the pill has no colour to offer.
const DEFAULT_MARK_COLOUR = '#00B0F0';

function injectMountTargets(html, inPlaceImageMap = {}) {
  const indices = [];
  const graphTarget = (n) => {
    const idx = parseInt(n, 10);
    indices.push(idx);
    return `<div data-ipg-root="${idx}" data-ipg-click-index="${idx}" style="width:100%;min-height:320px;overflow:visible;position:relative;box-sizing:border-box;"></div>`;
  };

  let modified = html;

  PORTAL_RE.lastIndex = 0;
  modified = modified.replace(PORTAL_RE, (_, n) => graphTarget(n));
  GRAPH_MARKER_RE.lastIndex = 0;
  modified = modified.replace(GRAPH_MARKER_RE, (_, n) => graphTarget(n));
  IMAGE_MARKER_RE.lastIndex = 0;
  modified = modified.replace(IMAGE_MARKER_RE, (_, n) => {
    const idx = parseInt(n, 10);
    const ipi = inPlaceImageMap[idx] || {};
    const src = ipi.presigned_url || '';
    const figNum = ipi.img_num ?? idx;
    const caption = ipi.subtitle
      ? `<div class="genbi-inplace-caption ev-fs-11 text-center mt-1"><b>Figure ${figNum}.</b> ${ipi.subtitle}</div>`
      : '';
    const footer = ipi.footer
      ? `<div class="genbi-inplace-footer ev-fs-11 ev-small-p-dark mt-1"><i class="bi bi-pencil-square me-1"></i><b>Note</b> ${ipi.footer}</div>`
      : '';
    return `<div class="genbi-inplace-figure" data-ipi-index="${idx}">`
      + caption
      + `<img class="genbi-inplace-img" src="${src}" alt="" />`
      + footer
      + `</div>`;
  });
  return { modified, indices };
}

/* ── Cell-level formatting helpers ─────────────────────────────────────────
   Everything here writes to the <td>/<th> style attribute, which is exactly
   what structured_parser.parse_cell_style() reads. Inline <span style> is NOT
   written inside cells: parse_element() has no span branch, so such a style
   would render on screen and then vanish in the DOCX. */

const rgbToHex = (v) => {
  if (!v) return null;
  const m = String(v).match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return v;
  const h = (x) => Number(x).toString(16).padStart(2, '0');
  return `#${h(m[1])}${h(m[2])}${h(m[3])}`.toUpperCase();
};

// Special-section merge override, read by special_section_html_builder._wants_merge.
// Keep these values and the limit in step with that file (MERGE_ATTR / MERGE_YES /
// MERGE_NO, and the special_section_columns default).
const MERGE_ATTR = 'data-merge';
const MERGE_YES = 'merge';
const MERGE_NO = 'do-not-merge';
const SPECIAL_SECTION_MERGE_LIMIT = 2;

// Colspan-expanded width of a table's own rows — table.rows never reaches into a
// nested table, which matches the builder's count.
const tableColumnCount = (table) =>
  Array.from(table.rows).reduce((widest, row) => Math.max(
    widest,
    Array.from(row.cells).reduce((sum, cell) => sum + (cell.colSpan || 1), 0)
  ), 0);

const mergesByDefault = (table) => tableColumnCount(table) <= SPECIAL_SECTION_MERGE_LIMIT;

const readMergeIntoSection = (table) => {
  const choice = (table.getAttribute(MERGE_ATTR) || '').trim().toLowerCase();
  if (choice === MERGE_YES) return true;
  if (choice === MERGE_NO) return false;
  return mergesByDefault(table);
};

// structured_parser skips text-align for header/footer rows, so the alignment
// control must be disabled there rather than silently dropping the value.
const isHeaderOrFooterCell = (cell) =>
  cell.tagName === 'TH' || !!cell.closest('thead') || !!cell.closest('tfoot');

/**
 * Return only the cells that belong to the outermost table inside `root`.
 * Cells inside nested tables (e.g. a <table> embedded in a <td>) are excluded
 * so formatting operations never spill into sub-tables.
 */
const getTopLevelCells = (root) => {
  if (!root) return [];
  const mainTable = root.querySelector('table');
  if (!mainTable) return [];
  return Array.from(root.querySelectorAll('td, th')).filter(
    cell => cell.closest('table') === mainTable
  );
};

const setCitationBadgesEditable = (root, editable) => {
  if (!root) return;
  root.querySelectorAll(CITATION_BADGE_SELECTOR).forEach(badge => {
    if (editable) {
      badge.setAttribute('contenteditable', 'false');
    } else {
      badge.removeAttribute('contenteditable');
    }
  });
};

const stripZeroWidthText = (root) => {
  if (!root) return;
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const emptyNodes = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.textContent.includes('\u200B')) continue;
    node.textContent = node.textContent.replace(/\u200B/g, '');
    if (!node.textContent) emptyNodes.push(node);
  }
  emptyNodes.forEach(node => node.remove());
};

/**
 * Drop one style property from the spans under `root`, unwrapping any span left
 * with nothing to say. `accept` limits it to the spans a caller wants (a
 * selection's range, say); without it every span under `root` is swept.
 * Returns true when something changed, so the caller can skip a no-op save.
 */
const stripSpanProp = (root, prop, accept = null) => {
  if (!root) return false;
  let changed = false;
  Array.from(root.querySelectorAll('span[style]')).forEach(sp => {
    if (accept && !accept(sp)) return;
    if (sp.style.getPropertyValue(prop)) {
      sp.style.removeProperty(prop);
      changed = true;
    }
    if (!sp.getAttribute('style')?.trim()) {
      while (sp.firstChild) sp.parentNode.insertBefore(sp.firstChild, sp);
      sp.remove();
      changed = true;
    }
  });
  return changed;
};

const readCellState = (cells) => {
  const first = cells[0];
  if (!first) return {};
  // Only report bgColor when it is a genuine inline-style value the user (or
  // the source HTML) set explicitly. Computed backgrounds inherited from
  // Bootstrap .table / .table-striped or browser defaults should not show up
  // in the pill swatch — they confuse users into thinking they applied a fill.
  const rawBg = first.style.backgroundColor;
  const bgHex = rawBg && !/transparent|rgba\(0,\s*0,\s*0,\s*0\)/i.test(rawBg)
    ? rgbToHex(rawBg)
    : null;
  return {
    textColor: rgbToHex(first.style.color) || null,
    bgColor: bgHex,
    align: first.style.textAlign || null,
    alignDisabled: cells.some(isHeaderOrFooterCell),
    cellCount: cells.length,
  };
};

const SUBTITLE_CITE_RE = /\[(doc_id_(\d+)|ar_id_(\d+))\]/g;

const renderSubtitleWithCitations = (text, docs = [], refs = []) => {
  if (!text) return text;
  // The backend now chips subtitle citations into badge spans (as it does for
  // figures); shorten their labels and render the HTML.
  if (/<span\b/i.test(text)) {
    const html = text.replace(
      /<span([^>]*\b(?:ev-message-card6|ev-message-card7)\b[^>]*)>([^<]*)<\/span>/g,
      (full, attrs, inner) => {
        const raw = (inner || '').trim();
        const short = raw.length > 20 ? raw.slice(0, 19) + '…' : raw;
        const withTitle = /\s(?:title|data-src)=/.test(attrs) ? attrs : `${attrs} title="${raw.replace(/"/g, '&quot;')}"`;
        return `<span${withTitle}>${short}</span>`;
      }
    );
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  }
  if (!SUBTITLE_CITE_RE.test(text)) return text;
  SUBTITLE_CITE_RE.lastIndex = 0;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = SUBTITLE_CITE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const isDoc = match[1].startsWith('doc_id');
    const id = parseInt(isDoc ? match[2] : match[3], 10);
    let displayName;
    if (isDoc) {
      const doc = docs.find(d => d.id === id);
      displayName = doc?.name || match[1];
    } else {
      const ref = refs.find(r => r.ar_id === id);
      displayName = ref?.title || match[1];
    }
    const truncated = displayName.length > 20 ? displayName.slice(0, 19) + '…' : displayName;
    parts.push(
      <span key={`cite-${match.index}`} className={isDoc ? 'ev-message-card6' : 'ev-message-card7'} title={displayName} data-ref={match[0]}>
        {truncated}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const TableComponent = ({
  tableData, isLoading, contentId, subtitle, footer = null,
  isEditing = false, onTableChange, onFieldChange, inPlaceGraphMap = {},
  inPlaceImageMap = {}, hideContentLabel = false,
  onInPlaceGraphChange,
  onInPlaceGraphView,
  onInPlaceGraphEdit,
  // Optional. When omitted (Canvas / Copilot / Genbook), every rich-format
  // code path below stays dormant and this component behaves exactly as before.
  // GenBI passes a renderer so TableComponent never imports a genbi/ module.
  renderFormatPill = null,
  // Optional, same opt-in contract as renderFormatPill: GenBI supplies the
  // control so TableComponent never imports a colour picker of its own.
  renderTableTools = null,
  docs = [], refs = [], onNeedReferences, refsLoading = false, onCreateReference,
  // Optional, edit mode only. (file) => Promise<{ index, presigned_url, subtitle, footer } | null>.
  // An image pasted into a cell is handed over (the caller shows the popup and
  // uploads) and an in-place figure for that index is put in the cell. Saving
  // collapses it to [[InPlace_ImageN]] like every other in-place figure.
  onPasteImage = null,
  // Optional, edit mode only. Called with an insert(source) function whenever the
  // caret is placed in a cell, so an "Insert image" button elsewhere can target it.
  onImageTarget = null,
  // Optional, view mode only. GenBI turns it on for special-section tables to show
  // whether the table merges into the section box (the edit-mode toggle sets it).
  showMergeStatus = false,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  const tableRef = useRef(null);
  const footerRef = useRef(null);
  const subtitleRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const rootsRef = useRef({});

  // ── Rich-format pill (cell mode) ─────────────────────────────────────────
  const [pillRect, setPillRect] = useState(null);
  const [pillMarks, setPillMarks] = useState({});
  const [pillCellState, setPillCellState] = useState({});
  const [pillOnCitation, setPillOnCitation] = useState(false);
  const selectedCellsRef = useRef([]);
  const [borderColor, setBorderColor] = useState(null);
  const [mergeIntoSection, setMergeIntoSection] = useState(false);
  // Read off the html string, not the rendered DOM, so view mode needs no effect.
  const mergeStatus = useMemo(() => {
    if (!showMergeStatus || isEditing || !tableData?.html) return null;
    const table = new DOMParser().parseFromString(tableData.html, 'text/html').querySelector('table');
    return table ? readMergeIntoSection(table) : null;
  }, [showMergeStatus, isEditing, tableData?.html]);

  const { modified: modifiedHtml, indices: portalIndices } = useMemo(
    () => tableData?.html ? injectMountTargets(tableData.html, inPlaceImageMap) : { modified: '', indices: [] },
    [tableData?.html, inPlaceImageMap]
  );

  // ── Freeze rendered HTML during an edit session ──────────────────────────
  // Cells are contentEditable inside dangerouslySetInnerHTML. If we let the live
  // modifiedHtml drive the render while editing, capturing edits pushes new html
  // to the parent, which recomputes modifiedHtml and rebuilds the DOM — dropping
  // the caret AND remounting in-place graph roots mid-edit. So we snapshot the
  // html at edit-start and render from that frozen value until editing ends.
  const frozenHtmlRef = useRef(modifiedHtml);
  const prevEditingRef = useRef(false);
  if (isEditing && !prevEditingRef.current) {
    frozenHtmlRef.current = modifiedHtml;   // snapshot when edit begins
  }
  if (!isEditing) {
    frozenHtmlRef.current = modifiedHtml;    // view mode follows live html
  }
  prevEditingRef.current = isEditing;
  const htmlToRender = isEditing ? frozenHtmlRef.current : modifiedHtml;

  // ── Theme observer ───────────────────────────────────────────────────────
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  // ── Unmount all React roots ──────────────────────────────────────────────
  const unmountAllRoots = useCallback(() => {
    const roots = Object.values(rootsRef.current).map(entry => entry.root);
    // Clear the registry synchronously so a fast remount never reuses these.
    rootsRef.current = {};
    deferUnmount(roots);
  }, []);

  // ── Standard Sanitized Document Re-sync Action ────────────────────────────
  const handleCellInput = useCallback(() => {
    if (!tableRef.current) return;
    const table = tableRef.current.querySelector('table');
    if (!table) return;

    const clone = table.cloneNode(true);
    clone.classList.remove('copilot-custom-table', 'table', 'table-sm', 'table-bordered', 'table-light', 'table-dark');
    if (!clone.className) clone.removeAttribute('class');
    
    clone.querySelectorAll('td, th').forEach(cell => {
      cell.removeAttribute('contenteditable');
      cell.classList.remove('genbi-table-cell-editable');
      if (!cell.className) cell.removeAttribute('class');

      // Drop the render-only colour the Bootstrap effect copies down from
      // <table style="color:...">. It is the only writer that uses !important,
      // so the priority identifies it, and structured_parser.normalize_hex_color
      // returns None for "#RRGGBB !important" anyway — baking it in would put a
      // value in the saved html that the DOCX parser silently discards.
      if (cell.style.getPropertyPriority('color') === 'important') {
        cell.style.removeProperty('color');
      }
      // Render-only too: the cell-scoped extend colour for in-place captions/footers.
      cell.style.removeProperty('--genbi-extend-color');
      if (!cell.getAttribute('style')?.trim()) cell.removeAttribute('style');

      // Strip edit-time placeholders injected for figure-only cells
      cell.querySelectorAll('.genbi-cell-text-ph').forEach(ph => ph.remove());
      setCitationBadgesEditable(cell, false);
      stripZeroWidthText(cell);

      cell.querySelectorAll('[data-ipg-root]').forEach(rootDiv => {
        const idx = rootDiv.getAttribute('data-ipg-root');
        const ph = clone.ownerDocument.createElement('div');
        ph.className = 'genbi-inplace-graph-portal';
        ph.setAttribute('data-ipg-index', idx);
        rootDiv.replaceWith(ph);
      });
    });

    if (onFieldChange) onFieldChange('html', clone.outerHTML);
    else if (onTableChange) onTableChange(tableData.table_id, clone.outerHTML);
  }, [onFieldChange, onTableChange, tableData?.table_id]);

  // ── Image pasted / dropped / picked into a cell ─────────────────────────
  // Hands the image to the caller (popup + upload), then puts an in-place figure for
  // the returned index at `range` in `cell` (or at the cell's end).
  const insertImageIntoCell = useCallback((cell, range, source) => {
    Promise.resolve(source.load())
      .catch((err) => {
        window.alert(err?.message || 'The pasted image could not be read.');
        return null;
      })
      .then((file) => (file ? onPasteImage(file) : null))
      .then((ipi) => {
        if (!ipi || ipi.index == null || !cell.isConnected) return;
        const { modified } = injectMountTargets(`[[InPlace_Image${ipi.index}]]`, { [ipi.index]: ipi });
        const holder = document.createElement('div');
        holder.innerHTML = modified;
        const figure = holder.firstElementChild;
        if (!figure) return;
        figure.setAttribute('contenteditable', 'false');
        if (range && cell.contains(range.startContainer)) {
          range.collapse(false);
          range.insertNode(figure);
        } else {
          cell.appendChild(figure);
        }
        handleCellInput();
      })
      .catch((err) => console.error('Pasted image was not inserted in the cell', err));
  }, [onPasteImage, handleCellInput]);

  const caretRangeIn = (cell) => {
    const sel = window.getSelection();
    return sel && sel.rangeCount > 0 && cell.contains(sel.getRangeAt(0).startContainer)
      ? sel.getRangeAt(0).cloneRange()
      : null;
  };

  const handleCellImagePaste = useCallback((e, dataTransfer) => {
    if (!isEditing || !onPasteImage || !tableRef.current) return;
    const source = imageSourceFromTransfer(dataTransfer);
    if (!source) return;
    const cell = e.target.closest?.('td, th');
    if (!cell || !tableRef.current.contains(cell)) return;
    e.preventDefault();
    e.stopPropagation();
    // The figure lands where the user pasted.
    insertImageIntoCell(cell, caretRangeIn(cell), source);
  }, [isEditing, onPasteImage, insertImageIntoCell]);

  // ── Citation pills: click selects one, copy/paste keep them as pills ─────
  const handlePillClick = useCallback((e) => {
    if (isEditing) selectClickedPill(e, e.currentTarget);
  }, [isEditing]);

  const handlePillCopy = useCallback((e) => {
    copySelectedPills(e, e.currentTarget);
  }, []);

  const handleCellPaste = useCallback((e) => {
    if (!isEditing || !tableRef.current) return;
    if (onPasteImage) {
      handleCellImagePaste(e, e.clipboardData);
      if (e.defaultPrevented) return;
    }
    const cell = e.target.closest?.('td, th');
    if (!cell || !tableRef.current.contains(cell)) return;
    const html = pastedPillsHTML(e.clipboardData, docs, refs, { editable: false });
    if (html == null) return;
    e.preventDefault();
    document.execCommand('insertHTML', false, html);
    handleCellInput();
  }, [isEditing, onPasteImage, handleCellImagePaste, docs, refs, handleCellInput]);

  const handleFooterPaste = useCallback((e) => {
    const html = pastedPillsHTML(e.clipboardData, docs, refs);
    if (html == null) return;
    e.preventDefault();
    document.execCommand('insertHTML', false, html);
    if (onFieldChange && footerRef.current) onFieldChange('footnote', footerRef.current.innerHTML);
  }, [docs, refs, onFieldChange]);

  // The last caret position in a cell, so the response's "Insert image" button
  // (which lives in the text editor's toolbar) can put the image in this cell.
  const lastCellCaretRef = useRef(null);
  const rememberCellCaret = useCallback((e) => {
    if (!isEditing || !onImageTarget || !tableRef.current) return;
    const cell = e.target.closest?.('td, th');
    if (!cell || !tableRef.current.contains(cell)) return;
    lastCellCaretRef.current = { cell, range: caretRangeIn(cell) };
    // Returns false when the cell has gone (edit closed, table re-rendered), so the
    // caller falls back to the text.
    onImageTarget((source) => {
      const last = lastCellCaretRef.current;
      if (!last || !last.cell.isConnected) return false;
      insertImageIntoCell(last.cell, last.range, source);
      return true;
    });
  }, [isEditing, onImageTarget, insertImageIntoCell]);

  // ── Mount graphs via clean React sub-roots ────────────────────────────
  useEffect(() => {
    if (!tableRef.current) return;

    const activeIndices = new Set(portalIndices);
    const staleRoots = [];
    Object.keys(rootsRef.current).forEach(key => {
      if (!activeIndices.has(Number(key))) {
        const entry = rootsRef.current[key];
        if (entry) {
          staleRoots.push(entry.root);
          delete rootsRef.current[key];
        }
      }
    });
    // Nothing re-mounts into these indices in this pass, so deferring is safe.
    deferUnmount(staleRoots);

    const mountInto = (n, container, element) => {
      const existing = rootsRef.current[n];
      if (existing && existing.container === container && container.isConnected) {
        try { existing.root.render(element); return; } catch (_) { /* fall through to recreate */ }
      }
      if (existing) {
        // Stays synchronous on purpose: a new root is created on `container`
        // immediately below, and when it is the same node a deferred unmount
        // would fire afterwards and tear down the fresh root. This path runs
        // from the effect body, not from the parent's commit, so it is safe.
        try { existing.root.unmount(); } catch (_) {}
        delete rootsRef.current[n];
      }
      try {
        const root = _createRoot(container);
        root.render(element);
        rootsRef.current[n] = { root, container };
      } catch (_) {}
    };

    portalIndices.forEach(n => {
      const container = tableRef.current.querySelector(`[data-ipg-root="${n}"]`);
      if (!container) return;

      const ipg = inPlaceGraphMap[n];

      if (!ipg?.figure_dict) {
       
        if (isEditing) {
          const placeholder = (
            <div className="ipg-removed-placeholder">
              <i className="bi bi-trash3"></i>
              <span>Figure removed — will be deleted on save</span>
            </div>
          );
          mountInto(n, container, placeholder);
        } else {
          const entry = rootsRef.current[n];
          if (entry) {
            delete rootsRef.current[n];
            // View mode, figure removed — nothing re-mounts here this pass.
            deferUnmount(entry.root);
          }
        }
        return;
      }

      const element = (
        <div className="genbi-ipg-portal-wrapper">
          <GraphComponent
            graphData={ipg}
            isLoading={false}
            contentId={ipg.img_num ?? n}
            subtitle={ipg.subtitle}
            footer={ipg.footer || null}
            isInPlace={true}
          />
          
          <div
            className={`ipg-click-interceptor ${isEditing ? 'is-editing' : 'is-viewing'}`}
          >
            <div
              className={isEditing ? 'ipg-edit-hint' : 'ipg-expand-hint'}
            >
              {isEditing ? 'Click to edit' : 'Click to expand'}
            </div>
          </div>
        </div>
      );

      mountInto(n, container, element);
    });
  }, [modifiedHtml, inPlaceGraphMap, portalIndices, isDarkMode, isEditing]);

  // ── Reliable in-place click & deep deletion handling (native capture phase) ──
  useEffect(() => {
    const node = tableRef.current;
    if (!node) return;

    const onCaptureClick = (e) => {
      
      // ── STANDARD MODAL SELECTION VIEW/EDIT REDIRECTS (in-place graphs) ──
      const root = e.target.closest?.('[data-ipg-root]');
      if (!root) return;
      const n = parseInt(
        root.getAttribute('data-ipg-click-index') ?? root.getAttribute('data-ipg-root'),
        10
      );
      if (Number.isNaN(n)) return;
      const ipg = inPlaceGraphMap[n];
      if (!ipg) return;

      e.stopPropagation();
      e.preventDefault();
      
      if (isEditing) {
        if (typeof onInPlaceGraphEdit === 'function') {
          const currentTitle = ipg.figure_dict?.layout?.title;
          const titleText = typeof currentTitle === 'string' ? currentTitle : (currentTitle?.text ?? '');
          onInPlaceGraphEdit({ ipg, localSubtitle: ipg.subtitle ?? '', localTitle: titleText });
        }
      } else if (typeof onInPlaceGraphView === 'function') {
        onInPlaceGraphView(ipg);
      }
    };

    node.addEventListener('click', onCaptureClick, true);
    return () => node.removeEventListener('click', onCaptureClick, true);
  }, [inPlaceGraphMap, isEditing, onInPlaceGraphView, onInPlaceGraphEdit]);

  // ── Unmount on component unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => unmountAllRoots();
  }, [unmountAllRoots]);

  // ── Bootstrap table classes + image styling ──────────────────────────────
  useEffect(() => {
    if (!tableRef.current || !tableData?.html) return;
    tableRef.current.querySelectorAll('table').forEach(table => {
      table.classList.remove('table', 'table-sm', 'table-light', 'table-dark');
      table.classList.add('copilot-custom-table', 'table', 'table-sm', 'table-bordered');
      // The extend/preserve merge puts its highlight on <table style="color:...">,
      // which Bootstrap's own td/th colour rule would otherwise beat. Pushing it
      // down is a render-only effect: handleCellInput strips anything marked
      // !important back out before the html is saved.
      const color = table.style.color;
      // Extend/Amend colour lives on <table style="color:..">. In-place figure
      // captions/footers have their own class colour (and graphs mount later via
      // portals), so expose it as a CSS var that GenBIStudio.css picks up.
      if (color) table.style.setProperty('--genbi-extend-color', color);
      else table.style.removeProperty('--genbi-extend-color');
      if (color) {
        table.querySelectorAll('td, th').forEach(cell => {
          if (!cell.querySelector('img')) cell.style.setProperty('color', color, 'important');
        });
      }
      // A colour on the cell itself (<td style="color:..">) is extended by the DOCX
      // writer over the in-place caption/footer runs in that cell, but on screen
      // their class colour wins over inheritance. Scope the same CSS var to the cell
      // so it overrides the table-level one. handleCellInput strips it before save.
      table.querySelectorAll('td, th').forEach(cell => {
        if (cell.closest('table') !== table || !cell.querySelector(FIGURE_CELL_SELECTOR)) return;
        // An !important colour is the table-level copy pushed down above, not the cell's own
        const cellColor = cell.style.getPropertyPriority('color') === 'important' ? '' : cell.style.color;
        if (cellColor) cell.style.setProperty('--genbi-extend-color', cellColor);
        else cell.style.removeProperty('--genbi-extend-color');
      });
      table.querySelectorAll('td img, th img').forEach(img => {
        if (img.closest('.genbi-inplace-figure')) return;
        img.style.maxWidth = '100%'; img.style.height = 'auto';
        img.style.display = 'block'; img.style.margin = 'auto';
        const cell = img.closest('td, th');
        if (cell) { cell.style.minWidth = '150px'; cell.style.verticalAlign = 'middle'; }
      });
      table.querySelectorAll('[data-ipg-root]').forEach(el => {
        // Let the cell grow to the chart's natural height instead of clipping it.
        el.style.minHeight = '320px';
        el.style.height = 'auto';
        el.style.maxHeight = 'none';
        el.style.overflow = 'visible';
        const cell = el.closest('td, th');
        if (cell) {
          cell.style.verticalAlign = 'top';
          cell.style.padding = '4px';
          cell.style.width = '280px';
          cell.style.minWidth = '240px';
          cell.style.maxWidth = '380px';
        }
      });

      // Text/content cells: ensure citation badge spans (ev-message-card6/7) and
      // other inline content wrap properly instead of forcing the column too narrow.
      // Cells with citation badges need extra min-width so badges aren't forced to
      // stack one-per-line in narrow viewports.
      table.querySelectorAll('td, th').forEach(cell => {
        if (!cell.querySelector('[data-ipg-root]') && !cell.querySelector('.genbi-inplace-figure')) {
          cell.style.wordBreak = 'break-word';
          cell.style.overflowWrap = 'break-word';
          cell.style.whiteSpace = 'normal';
          const hasCitationBadge = cell.querySelector(CITATION_BADGE_SELECTOR);
          cell.style.minWidth = hasCitationBadge ? '200px' : '120px';

          if (hasCitationBadge) {
            // Tighten vertical spacing on the badges themselves
            cell.querySelectorAll(CITATION_BADGE_SELECTOR).forEach(badge => {
              badge.style.marginTop = '2px';
              badge.style.marginBottom = '2px';
              badge.style.display = 'inline-block';
              if (isEditing) badge.setAttribute('contenteditable', 'false');
              else badge.removeAttribute('contenteditable');
            });
            // Remove <br> tags that land directly between citation badges,
            // which causes large vertical gaps in narrow cells
            cell.querySelectorAll('br').forEach(br => {
              const prev = br.previousSibling;
              const next = br.nextSibling;
              const prevIsBadge = prev?.nodeType === Node.ELEMENT_NODE &&
                prev.matches(CITATION_BADGE_SELECTOR);
              const nextIsBadge = next?.nodeType === Node.ELEMENT_NODE &&
                next.matches(CITATION_BADGE_SELECTOR);
              if (prevIsBadge || nextIsBadge) br.remove();
            });
          }
        }
      });
    });
  }, [tableData, isDarkMode, modifiedHtml, isEditing]);

  // ── Editable cells ───────────────────────────────────────────────────────
  // Top-level cells get the full editable treatment (class + placeholder).
  // Nested sub-table cells get only contenteditable so the cursor works there
  // and the pill can detect selections, but NOT the genbi-table-cell-editable
  // class (which adds a grey/bordered edit-mode visual that looks wrong on
  // nested cells that the user didn't style).
  useEffect(() => {
    if (!tableRef.current || !tableData?.html) return;

    // View mode: three container-scoped passes instead of two querySelectorAll
    // per cell. With hundreds of tables on screen the per-cell walk dominated.
    if (!isEditing) {
      const container = tableRef.current;
      container.querySelectorAll('[contenteditable]')
        .forEach(el => el.removeAttribute('contenteditable'));
      container.querySelectorAll('.genbi-table-cell-editable')
        .forEach(el => el.classList.remove('genbi-table-cell-editable'));
      container.querySelectorAll('.genbi-cell-text-ph')
        .forEach(ph => ph.remove());
      return;
    }

    const topCells = getTopLevelCells(tableRef.current);
    const topSet = new Set(topCells);
    const allCells = Array.from(tableRef.current.querySelectorAll('td, th'));

    allCells.forEach(cell => {
      cell.setAttribute('contenteditable', 'true');
      if (topSet.has(cell)) {
        cell.classList.add('genbi-table-cell-editable');
      }
      cell.querySelectorAll(FIGURE_CELL_SELECTOR).forEach(el => {
        el.setAttribute('contenteditable', 'false');
      });
      setCitationBadgesEditable(cell, true);

      // If the cell contains only in-place figures and no other editable text,
      // inject a placeholder so the user has a cursor target to type into.
      const hasFigure = cell.querySelector('.genbi-inplace-figure, [data-ipg-root]');
      if (hasFigure && !cell.querySelector('.genbi-cell-text-ph')) {
        const hasOtherContent = [...cell.childNodes].some(n =>
          (n.nodeType === Node.TEXT_NODE && n.textContent.trim() !== '') ||
          (n.nodeType === Node.ELEMENT_NODE &&
            !n.matches('.genbi-inplace-figure, [data-ipg-root], .genbi-inplace-graph-portal, .genbi-cell-text-ph'))
        );
        if (!hasOtherContent) {
          const ph = document.createElement('span');
          ph.className = 'genbi-cell-text-ph';
          ph.setAttribute('contenteditable', 'true');
          ph.dataset.placeholder = 'Add text…';
          cell.appendChild(ph);
        }
      }
    });
  }, [isEditing, tableData, modifiedHtml]);

  // ── Whole-table border colour ────────────────────────────────────────────
  // Written on the <table> element itself. handleCellInput clones that element
  // and only strips classes, so the style attribute survives into
  // editedResponse.tables[].html, where extract_table_border_color reads it.
  useEffect(() => {
    if (!renderTableTools || !tableRef.current) return;
    const table = tableRef.current.querySelector('table');
    setBorderColor(table ? (rgbToHex(table.style.borderColor) || null) : null);
  }, [renderTableTools, isEditing, tableData]);

  const applyBorderColor = useCallback((hex) => {
    if (!tableRef.current) return;
    // Apply to ALL tables (main + nested) so borders are consistent
    tableRef.current.querySelectorAll('table').forEach(table => {
      if (hex) table.style.borderColor = hex;
      else table.style.removeProperty('border-color');
    });
    setBorderColor(hex || null);
    handleCellInput();
  }, [handleCellInput]);

  // ── Special-section merge toggle ─────────────────────────────────────────
  // Written as data-merge on the outermost <table>, the same carrier the border
  // colour uses, so it reaches special_section_html_builder through the saved html.
  // Absent means the default rule: merge at SPECIAL_SECTION_MERGE_LIMIT columns or
  // fewer. The attribute is only kept when the user picks the other way.
  useEffect(() => {
    if (!renderTableTools || !tableRef.current) return;
    const table = tableRef.current.querySelector('table');
    setMergeIntoSection(table ? readMergeIntoSection(table) : false);
  }, [renderTableTools, isEditing, tableData]);

  const applyMergeIntoSection = useCallback((merge) => {
    if (!tableRef.current) return;
    const table = tableRef.current.querySelector('table');
    if (!table) return;
    if (merge === mergesByDefault(table)) table.removeAttribute(MERGE_ATTR);
    else table.setAttribute(MERGE_ATTR, merge ? MERGE_YES : MERGE_NO);
    setMergeIntoSection(merge);
    handleCellInput();
  }, [handleCellInput]);

  // ── Selection tracking: which cells does the current selection touch? ────
  const refreshSelection = useCallback(() => {
    // Don't clear pill when user is typing in a link popup input
    const _focused = document.activeElement;
    if (_focused && _focused.closest?.('.rft-link-popup')) return;

    // If Ctrl+Click multi-cell selection is active, just update pill position
    // (handles scroll — cells move but pill must follow)
    if (selectedCellsRef.current.length > 1 && tableRef.current?.querySelector('.genbi-cell-selected')) {
      const selected = selectedCellsRef.current.filter(c => c.isConnected);
      if (selected.length === 0) { setPillRect(null); return; }
      // Position near the last selected cell that's still on screen
      const lastVisible = [...selected].reverse().find(c => {
        const r = c.getBoundingClientRect();
        return r.top >= 0 && r.top <= window.innerHeight;
      }) || selected[selected.length - 1];
      const r = lastVisible.getBoundingClientRect();
      setPillRect({ left: r.left, top: r.top, width: r.width, height: r.height });
      return;
    }

    if (!renderFormatPill || !isEditing || !tableRef.current) {
      setPillRect(null); return;
    }
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setPillRect(null); return;
    }

    const range = sel.getRangeAt(0);
    if (!tableRef.current.contains(range.commonAncestorContainer)) {
      setPillRect(null); return;
    }

    // Determine which table the selection lives in. When the caret is inside a
    // nested sub-table we must scope cell detection to that sub-table so (a) the
    // outer parent <td> is never included (no colour spill) and (b) the nested
    // cells ARE included (pill still appears).
    const ancestor = range.commonAncestorContainer;
    const ancestorEl = ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentElement : ancestor;
    const mainTable = tableRef.current.querySelector('table');

    // Walk up from the ancestor to find the innermost <table> the selection sits
    // inside. closest() starts from the element itself, but when the ancestor IS
    // a <td>/<th> we need the table that owns that cell, not an even-outer one,
    // so we always start from the cell or the element and let closest() find it.
    const selCell = ancestorEl?.closest?.('td, th');
    const selTable = selCell ? selCell.closest('table') : (ancestorEl?.closest?.('table') || mainTable);


    let cells;
    if (selTable && selTable !== mainTable) {
      // Selection is inside a nested table — pick cells scoped to that table
      cells = Array.from(selTable.querySelectorAll('td, th'))
        .filter(c => c.closest('table') === selTable && range.intersectsNode(c));
    } else {
      // Selection is in the main table — use top-level cells, but exclude any
      // cell whose only intersection is because it parents a nested table that
      // contains the actual selection.
      cells = getTopLevelCells(tableRef.current)
        .filter(c => {
          if (!range.intersectsNode(c)) return false;
          const nestedTable = c.querySelector('table');
          if (nestedTable && ancestorEl && nestedTable.contains(ancestorEl)) return false;
          return true;
        });
    }
    if (!cells.length) {
      setPillRect(null); return;
    }

    const cellState = readCellState(cells);
    selectedCellsRef.current = cells;
    setPillCellState(cellState);
    setPillOnCitation(rangeIsSinglePill(range));

    // Detect inline colour/highlight by walking the DOM from the selection
    // anchor up to the cell. queryCommandValue('foreColor'/'backColor') is NOT
    // used because it returns the cell's own or inherited colour even when
    // there is no user-applied inline <span>, producing false-positive swatches.
    // cellState.textColor is safe as a fallback below: it reads the cell's own
    // style attribute, never a computed or inherited value, and that attribute is
    // where an amend made before the mark became a span left its highlight.
    let fgMark = null;
    let bgMark = null;
    const anchorNode = sel.anchorNode;
    const anchorEl = anchorNode && (anchorNode.nodeType === Node.TEXT_NODE
      ? anchorNode.parentElement : anchorNode);
    if (anchorEl) {
      let el = anchorEl;
      const cell = anchorEl.closest('td, th');
      while (el && el !== cell) {
        if (el.nodeType === Node.ELEMENT_NODE && el.tagName === 'SPAN' && el.style) {
          if (!fgMark && el.style.color) fgMark = rgbToHex(el.style.color);
          if (!bgMark && el.style.backgroundColor &&
              !/transparent|rgba\(0,\s*0,\s*0,\s*0\)/i.test(el.style.backgroundColor)) {
            bgMark = rgbToHex(el.style.backgroundColor);
          }
        }
        el = el.parentElement;
      }
    }

    setPillMarks({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      unorderedList: document.queryCommandState('insertUnorderedList'),
      orderedList: document.queryCommandState('insertOrderedList'),
      color: fgMark || cellState.textColor,
      bg: bgMark,
    });

    const r = range.getBoundingClientRect();
    if (!r || r.width + r.height === 0) { setPillRect(null); return; }
    // Raw viewport rect only. The pill decides for itself whether it is on
    // screen and which side to sit on — no clamping, so it cannot drift.
    setPillRect({ left: r.left, top: r.top, width: r.width, height: r.height });
  }, [isEditing, renderFormatPill]);

  useEffect(() => {
    if (!renderFormatPill || !isEditing) { setPillRect(null); return undefined; }
    const node = tableRef.current;
    if (!node) return undefined;
    const onUp = () => setTimeout(refreshSelection, 0);
    // rAF-throttled: scroll fires many times per frame and each raw call was a
    // separate setState + re-measure, which showed up as jitter.
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => { raf = 0; refreshSelection(); });
    };
    node.addEventListener('mouseup', onUp);
    node.addEventListener('keyup', onUp);
    // Fallback: when the selection lives inside a nested contenteditable (e.g. a
    // sub-table), some browsers don't always bubble mouseup through all layers.
    // A document-level listener ensures we still pick it up.
    const onDocUp = (e) => {
      const currentNode = tableRef.current;
      if (currentNode && currentNode.contains(e.target)) {
        setTimeout(refreshSelection, 0);
      }
    };
    document.addEventListener('mouseup', onDocUp);
    // Capture phase: follow every scrolling ancestor, not just the window.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    // Dismiss pill when clicking outside the table and outside the pill itself
    const onDocMouseDown = (e) => {
      const currentNode = tableRef.current;
      if (currentNode && currentNode.contains(e.target)) return;
      if (e.target.closest('.rft-pill-anchor, .rft-pill, .rft-fmt-btn, .rft-pill__shell')) return;
      try { window.getSelection()?.removeAllRanges(); } catch (_) {}
      setPillRect(null);
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    // Also listen for selectionchange as backup — debounced to avoid racing
    // with mouseup's refreshSelection (the browser can fire selectionchange
    // with a transiently collapsed selection mid-drag, then mouseup finalises
    // the real selection — without the delay the pill flickers or never shows).
    let selChangeTimer = 0;
    const onSelChange = () => {
      clearTimeout(selChangeTimer);
      selChangeTimer = setTimeout(() => {
        if (selectedCellsRef.current.length > 1) return;
        const _af = document.activeElement;
        if (_af && _af.closest?.('.rft-link-popup')) return;
        if (document.querySelector('.rft-pill-anchor--visible:hover, .rft-remove-menu')) return;
        refreshSelection();
      }, 80);
    };
    document.addEventListener('selectionchange', onSelChange);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      clearTimeout(selChangeTimer);
      node.removeEventListener('mouseup', onUp);
      node.removeEventListener('keyup', onUp);
      document.removeEventListener('mouseup', onDocUp);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('selectionchange', onSelChange);
    };
  }, [isEditing, renderFormatPill, refreshSelection]);

  // ── Multi-cell mark detection helper ──────────────────────────────────────
  // A mark is "active" when ALL selected cells have it on their full content.
  // Matches Word behavior — Bold highlights only when every cell is bold.
  const detectMultiCellMarks = useCallback((cells) => {
    if (!cells || cells.length === 0) return { bold: false, italic: false, underline: false, unorderedList: false, orderedList: false, color: null, bg: null, hyperlink: null, hyperlinkEl: null };
    const allBold = cells.every(c => {
      const text = c.textContent?.trim();
      if (!text) return true;
      const b = c.querySelector('b, strong');
      return b && b.textContent?.trim() === text;
    });
    const allItalic = cells.every(c => {
      const text = c.textContent?.trim();
      if (!text) return true;
      const i = c.querySelector('i, em');
      return i && i.textContent?.trim() === text;
    });
    const allUnderline = cells.every(c => {
      const text = c.textContent?.trim();
      if (!text) return true;
      const u = c.querySelector('u');
      return u && u.textContent?.trim() === text;
    });
    const allUnorderedList = cells.every(c => {
      const text = c.textContent?.trim();
      if (!text) return true;
      const ul = c.querySelector('ul');
      return ul && ul.textContent?.trim() === text;
    });
    const allOrderedList = cells.every(c => {
      const text = c.textContent?.trim();
      if (!text) return true;
      const ol = c.querySelector('ol');
      return ol && ol.textContent?.trim() === text;
    });
    return {
      bold: allBold, italic: allItalic, underline: allUnderline,
      unorderedList: allUnorderedList, orderedList: allOrderedList,
      color: null, bg: null, hyperlink: null, hyperlinkEl: null,
    };
  }, []);

  // ── Ctrl+Click / Ctrl+Drag multi-cell selection ───────────────────────────
  // Normal click = single-cell (existing behavior).
  // Ctrl+Click = toggle cell in/out of multi-cell set.
  // Ctrl+Hold+Move = paint-select cells as cursor moves over them.
  // Pill shows for the set; formatting applies to all selected cells.
  useEffect(() => {
    const node = tableRef.current;
    if (!renderFormatPill || !isEditing || !node) return;

    let ctrlDragging = false;
    let dragMode = 'paint'; // 'paint' = add cells, 'erase' = remove cells

    // Track the last cell user interacted with — pill positions near this cell
    let lastTouchedCell = null;

    const updatePill = () => {
      const selected = Array.from(node.querySelectorAll('.genbi-cell-selected'));
      if (selected.length === 0) {
        selectedCellsRef.current = [];
        setPillRect(null);
        return;
      }
      selectedCellsRef.current = selected;
      setPillCellState(readCellState(selected));
      setPillMarks(detectMultiCellMarks(selected));

      // Position pill near the last touched cell — always visible on screen
      const anchor = lastTouchedCell && selected.includes(lastTouchedCell)
        ? lastTouchedCell
        : selected[selected.length - 1];
      const r = anchor.getBoundingClientRect();
      setPillRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    };

    const onMouseDown = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        // Normal click — clear any multi-cell highlight
        node.querySelectorAll('.genbi-cell-selected').forEach(el =>
          el.classList.remove('genbi-cell-selected')
        );
        selectedCellsRef.current = [];
        ctrlDragging = false;
        return;
      }
      if (e.button !== 0) return;

      const cell = e.target.closest('td, th');
      if (!cell || !node.contains(cell)) return;
      // Skip cells that ARE graph/image containers — but only check direct children,
      // not nested table content (a parent cell containing a nested table with graphs
      // inside it should still be selectable at the parent level)
      const hasDirectGraphOrImage = Array.from(cell.children).some(ch =>
        ch.matches?.('[data-ipg-root], .genbi-inplace-figure')
      );
      if (hasDirectGraphOrImage) return;

      e.preventDefault();
      window.getSelection()?.removeAllRanges();

      // Determine drag mode: if starting cell was already selected → erase mode
      const wasSelected = cell.classList.contains('genbi-cell-selected');
      dragMode = wasSelected ? 'erase' : 'paint';

      // Toggle cell on click
      cell.classList.toggle('genbi-cell-selected');
      lastTouchedCell = cell;
      ctrlDragging = true;
      updatePill();
    };

    const onMouseMove = (e) => {
      if (!ctrlDragging) return;
      if (!(e.ctrlKey || e.metaKey) || e.buttons !== 1) {
        ctrlDragging = false;
        return;
      }

      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const cell = el.closest('td, th');
      if (!cell || !node.contains(cell)) return;
      const hasDirectGraphOrImage = Array.from(cell.children).some(ch =>
        ch.matches?.('[data-ipg-root], .genbi-inplace-figure')
      );
      if (hasDirectGraphOrImage) return;

      // Paint mode: add unselected cells. Erase mode: remove selected cells.
      if (dragMode === 'paint' && !cell.classList.contains('genbi-cell-selected')) {
        cell.classList.add('genbi-cell-selected');
        lastTouchedCell = cell;
        window.getSelection()?.removeAllRanges();
        updatePill();
      } else if (dragMode === 'erase' && cell.classList.contains('genbi-cell-selected')) {
        cell.classList.remove('genbi-cell-selected');
        lastTouchedCell = cell;
        window.getSelection()?.removeAllRanges();
        updatePill();
      }
    };

    const onMouseUp = () => {
      if (ctrlDragging) {
        ctrlDragging = false;
        updatePill();
      }
    };

    // Outside click clears multi-cell
    const onOutsideClick = (e) => {
      if (node.contains(e.target)) return;
      // Don't clear if click is on the pill or any of its children
      if (e.target.closest('.rft-pill-anchor')) return;
      if (e.target.closest('.rft-pill')) return;
      if (e.target.closest('.rft-fmt-btn')) return;
      if (e.target.closest('.rft-pill__shell')) return;
      node.querySelectorAll('.genbi-cell-selected').forEach(el =>
        el.classList.remove('genbi-cell-selected')
      );
      selectedCellsRef.current = [];
    };

    node.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousedown', onOutsideClick);
    return () => {
      node.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousedown', onOutsideClick);
      node.querySelectorAll('.genbi-cell-selected').forEach(el =>
        el.classList.remove('genbi-cell-selected')
      );
      ctrlDragging = false;
    };
  }, [isEditing, renderFormatPill]);

  // ── Apply handlers ───────────────────────────────────────────────────────
  // Single-cell: execCommand on browser selection (existing behavior).
  // Multi-cell (Ctrl+select): loop each cell, select full content, execCommand.
  // For multi-cell: DON'T call refreshSelection after — it would see collapsed
  // selection and close the pill. The pill + selectedCellsRef are already correct.
  const isListCmd = (cmd) => cmd === 'insertUnorderedList' || cmd === 'insertOrderedList';

  // Browsers create a separate <ol>/<ul> per <br>-separated line when
  // execCommand fires inside a contentEditable cell. Merge adjacent same-type
  // lists so numbering is continuous instead of restarting for every item.
  const mergeAdjacentLists = useCallback((cell) => {
    if (!cell) return;
    let merged = true;
    while (merged) {
      merged = false;
      for (const list of cell.querySelectorAll('ol, ul')) {
        const next = list.nextElementSibling;
        if (next && next.tagName === list.tagName) {
          while (next.firstChild) list.appendChild(next.firstChild);
          next.remove();
          merged = true;
          break;
        }
      }
    }
  }, []);

  const renumberColumnOls = useCallback((anyCell) => {
    if (!anyCell) return;
    const table = anyCell.closest('table');
    if (!table) return;
    const row = anyCell.closest('tr');
    if (!row) return;
    const siblingCells = Array.from(row.querySelectorAll(':scope > td, :scope > th'));
    const colIdx = siblingCells.indexOf(anyCell);
    if (colIdx < 0) return;

    const allRows = Array.from(table.querySelectorAll(
      ':scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr'
    ));
    let counter = 1;
    allRows.forEach(r => {
      const cells = Array.from(r.querySelectorAll(':scope > td, :scope > th'));
      const targetCell = cells[colIdx];
      if (!targetCell) return;
      const ol = targetCell.querySelector('ol');
      if (!ol) return;
      ol.setAttribute('start', String(counter));
      counter += ol.querySelectorAll(':scope > li').length;
    });
  }, []);

  const execInline = useCallback((cmd) => {
    const cells = selectedCellsRef.current;
    if (!cells || cells.length === 0) return;
    try { document.execCommand('styleWithCSS', false, false); } catch (_) {}
    if (cells.length === 1) {
      document.execCommand(cmd);
      if (isListCmd(cmd)) {
        mergeAdjacentLists(cells[0]);
        if (cmd === 'insertOrderedList') renumberColumnOls(cells[0]);
      }
      handleCellInput();
      refreshSelection();
    } else {
      const sel = window.getSelection();
      cells.forEach(cell => {
        const range = document.createRange();
        range.selectNodeContents(cell);
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand(cmd);
      });
      if (isListCmd(cmd)) {
        cells.forEach(cell => mergeAdjacentLists(cell));
        if (cmd === 'insertOrderedList') renumberColumnOls(cells[0]);
      }
      sel.removeAllRanges();
      handleCellInput();
      // Re-apply highlight after all DOM updates settle (browser + React)
      const savedCells = [...cells];
      requestAnimationFrame(() => {
        savedCells.forEach(c => {
          if (c.isConnected) c.classList.add('genbi-cell-selected');
        });
        setPillMarks(detectMultiCellMarks(savedCells.filter(c => c.isConnected)));
      });
    }
  }, [handleCellInput, refreshSelection, detectMultiCellMarks, mergeAdjacentLists, renumberColumnOls]);

  const execInlineColour = useCallback((cmd, hex) => {
    const cells = selectedCellsRef.current;
    if (!cells || cells.length === 0) return;
    try { document.execCommand('styleWithCSS', false, true); } catch (_) {}
    const applyCmd = () => {
      if (cmd === 'hiliteColor' && !document.execCommand('hiliteColor', false, hex)) {
        document.execCommand('backColor', false, hex);
      } else if (cmd !== 'hiliteColor') {
        document.execCommand(cmd, false, hex);
      }
    };
    if (cells.length === 1) {
      applyCmd();
      try { document.execCommand('styleWithCSS', false, false); } catch (_) {}
      handleCellInput();
      refreshSelection();
    } else {
      const sel = window.getSelection();
      cells.forEach(cell => {
        const range = document.createRange();
        range.selectNodeContents(cell);
        sel.removeAllRanges();
        sel.addRange(range);
        applyCmd();
      });
      sel.removeAllRanges();
      try { document.execCommand('styleWithCSS', false, false); } catch (_) {}
      handleCellInput();
      const savedCells = [...cells];
      requestAnimationFrame(() => {
        savedCells.forEach(c => {
          if (c.isConnected) c.classList.add('genbi-cell-selected');
        });
        setPillMarks(detectMultiCellMarks(savedCells.filter(c => c.isConnected)));
      });
    }
  }, [handleCellInput, refreshSelection, detectMultiCellMarks]);

  // Spans only. Colour set on the cell element itself (the amend/extend
  // highlight) is cleared by handleCellRemove via setCellProp.
  const clearInlineProp = useCallback((prop) => {
    const cells = selectedCellsRef.current;
    if (!cells || cells.length === 0) return;
    if (cells.length === 1) {
      const sel = window.getSelection && window.getSelection();
      const range = (sel && sel.rangeCount > 0) ? sel.getRangeAt(0) : null;
      Array.from(cells[0].querySelectorAll('span[style]')).forEach(sp => {
        if (range && !range.intersectsNode(sp)) return;
        sp.style.removeProperty(prop);
        if (!sp.getAttribute('style')?.trim()) {
          while (sp.firstChild) sp.parentNode.insertBefore(sp.firstChild, sp);
          sp.remove();
        }
      });
      handleCellInput();
      refreshSelection();
    } else {
      cells.forEach(cell => stripSpanProp(cell, prop));
      handleCellInput();
      const savedCells = [...cells];
      requestAnimationFrame(() => {
        savedCells.forEach(c => {
          if (c.isConnected) c.classList.add('genbi-cell-selected');
        });
        setPillMarks(detectMultiCellMarks(savedCells.filter(c => c.isConnected)));
      });
    }
  }, [handleCellInput, refreshSelection, detectMultiCellMarks]);

  const setCellProp = useCallback((prop, value) => {
    const cells = selectedCellsRef.current;
    const isMulti = cells.length > 1;
    cells.forEach(cell => {
      if (value) cell.style[prop] = value;
      else cell.style.removeProperty(prop.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`));
    });
    handleCellInput();
    if (isMulti) {
      const savedCells = [...cells];
      requestAnimationFrame(() => {
        savedCells.forEach(c => {
          if (c.isConnected) c.classList.add('genbi-cell-selected');
        });
      });
    } else {
      refreshSelection();
    }
  }, [handleCellInput, refreshSelection]);

  const getTable = useCallback(() => tableRef.current?.querySelector('table'), []);

  const getTableAtPoint = useCallback((x, y) => {
    if (!tableRef.current) return getTable();
    const tables = Array.from(tableRef.current.querySelectorAll('table'));
    for (let i = tables.length - 1; i >= 0; i--) {
      const rect = tables[i].getBoundingClientRect();
      if (x >= rect.left - 25 && x <= rect.right + 10 && y >= rect.top - 25 && y <= rect.bottom + 10) {
        return tables[i];
      }
    }
    return getTable();
  }, [getTable]);

  // ── Single source of truth for row queries ──────────────────────────
  const getBodyRows = useCallback((table) => {
    if (!table) return [];
    const tbody = table.querySelector('tbody');
    if (tbody) return Array.from(tbody.querySelectorAll(':scope > tr'));
    return Array.from(table.querySelectorAll(':scope > tr')).filter(r => !r.closest('thead') && !r.closest('tfoot'));
  }, []);

  const getAllDirectRows = useCallback((table) => {
    if (!table) return [];
    return Array.from(table.querySelectorAll(':scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr'));
  }, []);

  const addRow = useCallback((position, targetTable) => {
    const table = targetTable || getTable();
    if (!table) return;
    const rows = getBodyRows(table);
    if (!rows.length) return;
    let refRow, insertAfter;
    if (position === 'below') { refRow = rows[rows.length - 1]; insertAfter = true; }
    else if (position === 'above') { refRow = rows[0]; insertAfter = false; }
    else {
      const idx = Math.min(Math.max(0, position), rows.length);
      if (idx >= rows.length) { refRow = rows[rows.length - 1]; insertAfter = true; }
      else { refRow = rows[idx]; insertAfter = false; }
    }
    const colCount = rows[0].querySelectorAll(':scope > td, :scope > th').length;
    const newRow = table.ownerDocument.createElement('tr');
    for (let i = 0; i < colCount; i++) {
      const cell = table.ownerDocument.createElement('td');
      cell.innerHTML = '&nbsp;';
      cell.setAttribute('contenteditable', 'true');
      cell.classList.add('genbi-table-cell-editable');
      newRow.appendChild(cell);
    }
    insertAfter ? refRow.after(newRow) : refRow.before(newRow);
    handleCellInput();
  }, [getTable, getBodyRows, handleCellInput]);

  const buildGridMap = useCallback((table) => {
    const rows = Array.from(table.querySelectorAll(':scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr'));
    const rowspanTracker = {};
    const map = [];
    let totalCols = 0;

    rows.forEach((row, ri) => {
      const cells = Array.from(row.querySelectorAll(':scope > td, :scope > th'));
      const rowMap = [];
      let logCol = 0;
      let cellIdx = 0;

      while (cellIdx < cells.length || logCol in rowspanTracker) {
        while (rowspanTracker[logCol] && rowspanTracker[logCol].remaining > 0) {
          const rs = rowspanTracker[logCol];
          const span = parseInt(rs.cell.getAttribute('colspan') || '1', 10);
          rowMap.push({ cell: null, logStart: logCol, logEnd: logCol + span - 1, coveredBy: rs.cell });
          rs.remaining--;
          if (rs.remaining <= 0) delete rowspanTracker[logCol];
          logCol += span;
        }

        if (cellIdx >= cells.length) break;

        const cell = cells[cellIdx];
        const cs = parseInt(cell.getAttribute('colspan') || '1', 10);
        const rs = parseInt(cell.getAttribute('rowspan') || '1', 10);

        rowMap.push({ cell, logStart: logCol, logEnd: logCol + cs - 1, rs: rs });

        if (rs > 1) {
          for (let c = 0; c < cs; c++) {
            rowspanTracker[logCol + c] = { remaining: rs - 1, cell };
          }
        }

        logCol += cs;
        cellIdx++;
      }

      while (rowspanTracker[logCol] && rowspanTracker[logCol].remaining > 0) {
        const rs = rowspanTracker[logCol];
        const span = parseInt(rs.cell.getAttribute('colspan') || '1', 10);
        rowMap.push({ cell: null, logStart: logCol, logEnd: logCol + span - 1, coveredBy: rs.cell });
        rs.remaining--;
        if (rs.remaining <= 0) delete rowspanTracker[logCol];
        logCol += span;
      }

      totalCols = Math.max(totalCols, logCol);
      map.push(rowMap);
    });

    return { totalCols, map, rows };
  }, []);

  const deleteRow = useCallback((rowIndex, targetTable) => {
    const table = targetTable || getTable();
    if (!table) return;
    const { map } = buildGridMap(table);
    const targetRows = getBodyRows(table);
    if (targetRows.length <= 1 || rowIndex < 0 || rowIndex >= targetRows.length) return;

    const rowToDelete = targetRows[rowIndex];
    const allRows = getAllDirectRows(table);
    const fullRowIdx = allRows.indexOf(rowToDelete);
    if (fullRowIdx < 0 || fullRowIdx >= map.length) { rowToDelete.remove(); handleCellInput(); return; }

    const rowMap = map[fullRowIdx];
    for (const entry of rowMap) {
      if (entry.cell === null && entry.coveredBy) {
        const rs = parseInt(entry.coveredBy.getAttribute('rowspan') || '1', 10);
        if (rs > 1) entry.coveredBy.setAttribute('rowspan', String(rs - 1));
      } else if (entry.cell && entry.rs && entry.rs > 1) {
        const nextRowIdx = fullRowIdx + 1;
        if (nextRowIdx < allRows.length) {
          const nextRow = allRows[nextRowIdx];
          entry.cell.setAttribute('rowspan', String(entry.rs - 1));
          if (entry.rs - 1 <= 1) entry.cell.removeAttribute('rowspan');
          let inserted = false;
          if (nextRowIdx < map.length) {
            for (const nextEntry of map[nextRowIdx]) {
              if (nextEntry.cell && nextEntry.logStart >= entry.logStart) {
                nextRow.insertBefore(entry.cell, nextEntry.cell);
                inserted = true;
                break;
              }
            }
          }
          if (!inserted) nextRow.appendChild(entry.cell);
        }
      }
    }
    rowToDelete.remove();
    handleCellInput();
  }, [getTable, getBodyRows, getAllDirectRows, buildGridMap, handleCellInput]);


  const addColumn = useCallback((position, targetTable) => {
    const table = targetTable || getTable();
    if (!table) return;
    const { totalCols, map, rows } = buildGridMap(table);

    const logicalIdx = position === 'right' ? totalCols : position === 'left' ? 0 : position;

    map.forEach((rowMap, ri) => {
      const row = rows[ri];
      const isHeader = row.querySelector('th') !== null;
      const tag = isHeader ? 'th' : 'td';
      let handled = false;
      for (const entry of rowMap) {
        if (entry.logStart <= logicalIdx && logicalIdx <= entry.logEnd && entry.cell === null) {
          const cs = parseInt(entry.coveredBy.getAttribute('colspan') || '1', 10);
          entry.coveredBy.setAttribute('colspan', String(cs + 1));
          handled = true;
          break;
        }
        if (entry.cell && entry.logStart < logicalIdx && logicalIdx <= entry.logEnd) {
          const cs = parseInt(entry.cell.getAttribute('colspan') || '1', 10);
          entry.cell.setAttribute('colspan', String(cs + 1));
          handled = true;
          break;
        }
        if (entry.cell && entry.logStart === logicalIdx) {
          const newCell = table.ownerDocument.createElement(tag);
          newCell.innerHTML = '&nbsp;';
          if (isEditing) { newCell.setAttribute('contenteditable', 'true'); newCell.classList.add('genbi-table-cell-editable'); }
          row.insertBefore(newCell, entry.cell);
          handled = true;
          break;
        }
      }
      if (!handled) {
        const newCell = table.ownerDocument.createElement(tag);
        newCell.innerHTML = '&nbsp;';
        if (isEditing) { newCell.setAttribute('contenteditable', 'true'); newCell.classList.add('genbi-table-cell-editable'); }
        row.appendChild(newCell);
      }
    });
    handleCellInput();
  }, [getTable, buildGridMap, handleCellInput, isEditing]);

  const deleteColumn = useCallback((colIndex, targetTable) => {
    const table = targetTable || getTable();
    if (!table) return;
    const { totalCols, map, rows } = buildGridMap(table);
    if (totalCols <= 1) return;

    map.forEach((rowMap, ri) => {
      for (const entry of rowMap) {
        if (entry.logStart <= colIndex && colIndex <= entry.logEnd) {
          if (entry.cell === null && entry.coveredBy) {
            // Covered by rowspan — shrink the spanning cell's colspan
            const cs = parseInt(entry.coveredBy.getAttribute('colspan') || '1', 10);
            if (cs > 1) entry.coveredBy.setAttribute('colspan', String(cs - 1));
            else entry.coveredBy.remove();
          } else if (entry.cell) {
            const cs = parseInt(entry.cell.getAttribute('colspan') || '1', 10);
            if (cs > 1) entry.cell.setAttribute('colspan', String(cs - 1));
            else entry.cell.remove();
          }
          break;
        }
      }
    });
    handleCellInput();
  }, [getTable, buildGridMap, handleCellInput]);

  const [hoverIndicator, setHoverIndicator] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  // The menu is portalled to <body> with position:fixed so the table card's overflow
  // can't clip it (right-click on the last row cut it off). Opens at the cursor and
  // flips up/left when it would run past the viewport edge.
  const positionContextMenu = useCallback((el) => {
    if (!el || !contextMenu) return;
    const pad = 8;
    let left = contextMenu.clientX;
    let top = contextMenu.clientY;
    if (left + el.offsetWidth + pad > window.innerWidth) left = Math.max(pad, left - el.offsetWidth);
    if (top + el.offsetHeight + pad > window.innerHeight) top = Math.max(pad, top - el.offsetHeight);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.visibility = 'visible';
  }, [contextMenu]);

  const handleTableMouseMove = useCallback((e) => {
    if (!isEditing) return;

    // If mouse is over the +/- button group, keep indicator alive
    if (e.target.closest('.tbl-btn-group') || e.target.closest('.tbl-insert-btn') || e.target.closest('.tbl-delete-btn')) return;

    const mx = e.clientX, my = e.clientY;
    const table = getTableAtPoint(mx, my);
    if (!table) return;
    const tableRect = table.getBoundingClientRect();
    const EDGE = 10;

    // Column "+" — only trigger when mouse is near the TOP of the table (within 30px)
    const firstRow = table.querySelector('thead > tr') || table.querySelector('tr');
    const firstCells = firstRow ? firstRow.querySelectorAll(':scope > td, :scope > th') : [];
    if (my >= tableRect.top - 30 && my <= tableRect.top + 30) {
      for (let i = 0; i < firstCells.length; i++) {
        const cr = firstCells[i].getBoundingClientRect();
        if (Math.abs(mx - cr.right) < EDGE) {
          setHoverIndicator({ type: 'col', index: i + 1, colRect: cr, tableRect, targetTable: table });
          return;
        }
      }
    }

    // Row "+" — only trigger when mouse is near the LEFT of the table (within 30px)
    const rows = getBodyRows(table);

    if (mx >= tableRect.left - 30 && mx <= tableRect.left + 30) {
      // Also detect bottom edge of thead last row
      const thead = table.querySelector('thead');
      if (thead) {
        const theadRows = thead.querySelectorAll(':scope > tr');
        const lastTheadRow = theadRows[theadRows.length - 1];
        if (lastTheadRow) {
          const hr = lastTheadRow.getBoundingClientRect();
          if (Math.abs(my - hr.bottom) < EDGE) {
            setHoverIndicator({ type: 'row', index: 0, rowRect: hr, tableRect, targetTable: table });
            return;
          }
        }
      }

      for (let i = 0; i < rows.length; i++) {
        const rr = rows[i].getBoundingClientRect();
        if (Math.abs(my - rr.bottom) < EDGE) {
          setHoverIndicator({ type: 'row', index: i + 1, rowRect: rr, tableRect, targetTable: table });
          return;
        }
      }
    }

    setHoverIndicator(null);
  }, [isEditing, getTableAtPoint, getBodyRows]);

  const handleTableMouseLeave = useCallback(() => setHoverIndicator(null), []);

  // B/I/U are toggled by their own buttons, so only these three reach here.
  const handleCellRemove = useCallback((key) => {
    if (key === 'cellbg') { setCellProp('backgroundColor', null); return; }
    clearInlineProp(key === 'bg' ? 'background-color' : 'color');
    // The amend/extend merge writes its highlight onto the cell's own style
    // attribute, not a span, so clearing spans alone leaves it on screen.
    if (key !== 'color') return;

    // An amend marks its changes with a <span style="color:.."> per changed run,
    // so "Remove text colour" sweeps every span in the table rather than only the
    // ones the selection touches: the marks are one amend's worth of colour and
    // read as a single thing to take off, cell by cell being unusable for a table
    // marked in twenty places.
    // ponytail: this takes the reader's own colouring with it. Split the sweep by
    // colour value if anyone ever wants to keep their own and drop only the mark.
    let changed = stripSpanProp(tableRef.current, 'color');

    // Amends made before the mark became a span wrote it on the cell instead.
    tableRef.current?.querySelectorAll('td, th').forEach(cell => {
      if (!cell.style.color) return;
      cell.style.removeProperty('color');
      changed = true;
    });
    setCellProp('color', null);

    // Extend/preserve puts its highlight on the <table> instead, and the render
    // effect copies that onto every cell, so clearing the cell alone changes
    // nothing on screen. It is a whole-table "this is new" marker, so take it off
    // the table and drop the copies it already pushed down.
    // ponytail: one cell's Remove clears the whole table's extend colour. Needs a
    // table-level control of its own if per-cell removal is ever wanted.
    tableRef.current?.querySelectorAll('table').forEach(table => {
      if (!table.style.color) return;
      table.style.removeProperty('color');
      table.querySelectorAll('td, th').forEach(cell => {
        if (cell.style.getPropertyPriority('color') === 'important') {
          cell.style.removeProperty('color');
        }
      });
      changed = true;
    });
    if (changed) handleCellInput();
  }, [setCellProp, clearInlineProp, handleCellInput]);

  /**
   * Colour the whole selected table the way an amend marks its changes: one span
   * per cell, never nested, so what the editor writes here and what the merge
   * writes in the backend read as the same thing on the way to the DOCX.
   *
   * The colour is the mark already on the table when there is one, so a table
   * touched up by hand keeps the amend's colour, then the pill's own swatch, then
   * the default. Cells holding a figure are skipped — moving a mounted portal
   * into a span would remount it.
   */
  const handleCellMark = useCallback(() => {
    const table = selectedCellsRef.current[0]?.closest('table')
      || tableRef.current?.querySelector('table');
    if (!table) return;
    const colour = rgbToHex(table.style.color)
      || rgbToHex(table.querySelector('span[style*="color"]')?.style.color)
      || pillMarks.color
      || DEFAULT_MARK_COLOUR;

    stripSpanProp(table, 'color');
    Array.from(table.querySelectorAll('td, th'))
      .filter(cell => cell.closest('table') === table)
      .forEach(cell => {
        if (!cell.textContent.trim()) return;
        // A span may not wrap a table, and a mounted figure may not be moved.
        if (cell.querySelector(`table, ${FIGURE_CELL_SELECTOR}`)) return;
        const span = document.createElement('span');
        span.style.color = colour;
        while (cell.firstChild) span.appendChild(cell.firstChild);
        cell.appendChild(span);
      });
    handleCellInput();
    setPillRect(null);
  }, [handleCellInput, pillMarks.color]);

  const copyTable = () => {
    if (!tableRef.current) return;
    const table = tableRef.current.querySelector('table');
    if (!table) return;
    const rows = [];
    table.querySelectorAll('tr').forEach(row => {
      const cells = [];
      row.querySelectorAll('td, th').forEach(c => cells.push(c.textContent.trim()));
      rows.push(cells.join('\t'));
    });
    navigator.clipboard.writeText(rows.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const exportCSV = () => {
    if (!tableRef.current) return;
    const table = tableRef.current.querySelector('table');
    if (!table) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(table);
    XLSX.utils.book_append_sheet(wb, ws, 'Table');
    XLSX.writeFile(wb, `table_${tableData.table_number || 'data'}.xlsx`);
  };

  if (isLoading) {
    return (
      <div className="copilot-table-container text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ev-reg-p-dark d-block mt-2">Generating Table</span>
      </div>
    );
  }

  if (!tableData || !tableData.html) {
    return (
      <div className="container-error">
        <div className="text-center"><p>Error loading table</p></div>
      </div>
    );
  }

  return (
    <div className="copilot-table-container">
      {(isEditing || subtitle) && (
        <div className="mb-2">
          {isEditing ? (
            <div className="genbi-figure-edit-field">
              <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                <i className="bi bi-justify"></i> <b>Table {contentId} — Subtitle</b>
              </span>
              <textarea
                ref={subtitleRef}
                className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                value={subtitle || ''}
                onChange={(e) => onFieldChange?.('subtitle', e.target.value)}
                rows={2}
                placeholder="Edit subtitle..."
              />
            </div>
          ) : (
            <span className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
              <i className="bi bi-justify"></i> <b>Table {contentId}. </b>{renderSubtitleWithCitations(subtitle, docs, refs)}
            </span>
          )}
        </div>
      )}
      
      {isEditing && (
        <div className="genbi-table-edit-banner">
          <i className="bi bi-pencil-square me-2"></i>
          <span>Click any cell to edit · Ctrl+Click to select multiple · Hover edges to insert or remove</span>
        </div>
      )}

      <div className="text-end mb-2 gap-2 d-flex align-items-center justify-content-end">
        {isEditing && renderTableTools
          && renderTableTools({
            borderColor,
            onBorderColor: applyBorderColor,
            mergeIntoSection,
            onMergeIntoSection: applyMergeIntoSection,
          })}
        {/* Shown only for a merging table: separate is the ordinary look, so it
            carries no badge. */}
        {mergeStatus && (
          <span
            className="genbi-merge-pill ev-bold-reg is-on"
            data-tooltip="Rows merge into the special-section box"
          >
            Merge
          </span>
        )}
        <button type="button" className="ev-btn-text-only-fill ev-fs-11 ev-bold-reg" onClick={copyTable}>
          {copied ? 'Copied!' : 'Copy'} <i className={`bi ${copied ? 'bi-check2' : 'bi-copy'}`}></i>
        </button>
        <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall" onClick={exportCSV}>
          Export XLSX <i className="bi bi-download"></i>
        </button>
      </div>

      <div className="position-relative pt-4 ps-4 pe-4 pb-4"
        onMouseMove={handleTableMouseMove}
        onMouseLeave={handleTableMouseLeave}
      >
        <div className="overflow-auto" onScroll={() => setHoverIndicator(null)}>
          <div
            ref={tableRef}
            dangerouslySetInnerHTML={{ __html: htmlToRender }}
            onBlur={handleCellInput}
            onPaste={isEditing ? handleCellPaste : undefined}
            onCopy={handlePillCopy}
            onClick={isEditing ? handlePillClick : undefined}
            onDrop={isEditing && onPasteImage ? (e) => handleCellImagePaste(e, e.dataTransfer) : undefined}
            onMouseUp={isEditing && onImageTarget ? rememberCellCaret : undefined}
            onKeyUp={isEditing && onImageTarget ? rememberCellCaret : undefined}
            onContextMenu={isEditing ? (e) => {
              const cell = e.target.closest('td, th');
              if (!cell) return;
              e.preventDefault();
              const table = cell.closest('table');
              if (!table) return;
              const row = cell.closest('tr');
              const rows = getBodyRows(table);
              const rowIdx = rows.indexOf(row);
              const cells = Array.from(row.querySelectorAll(':scope > td, :scope > th'));
              const colIdx = cells.indexOf(cell);
              // Compute logical column index for colspan-aware operations
              let logicalColIdx = 0;
              for (let ci = 0; ci < colIdx && ci < cells.length; ci++) {
                logicalColIdx += parseInt(cells[ci].getAttribute('colspan') || '1', 10);
              }
              const wr = tableRef.current.parentElement.parentElement.getBoundingClientRect();
              setContextMenu({
                x: e.clientX - wr.left, y: e.clientY - wr.top,
                clientX: e.clientX, clientY: e.clientY,
                rowIdx, colIdx: logicalColIdx,
                rowCount: rows.length,
                colCount: cells.reduce((s, c) => s + parseInt(c.getAttribute('colspan') || '1', 10), 0),
                targetTable: table, targetRow: row,
              });
            } : undefined}
          />
        </div>

        {/* Word-style "+" indicators */}
        {isEditing && hoverIndicator && tableRef.current && (() => {
          const wr = tableRef.current.parentElement.parentElement.getBoundingClientRect();
          const { type, tableRect } = hoverIndicator;

          if (type === 'col') {
            const { colRect } = hoverIndicator;
            const lineLeft = colRect.right - wr.left;
            const lineTop = tableRect.top - wr.top;
            const lineHeight = tableRect.height;
            return (
              <>
                <div className="tbl-insert-line-v position-absolute" style={{ left: lineLeft, top: lineTop, height: lineHeight }} />
                <div className="tbl-btn-group position-absolute d-flex gap-1" style={{ left: lineLeft - 22, top: lineTop - 24 }}>
                  <button type="button" className="tbl-insert-btn"
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={e => { e.stopPropagation(); addColumn(hoverIndicator.index, hoverIndicator.targetTable); setHoverIndicator(null); }}
                    title="Insert column here">
                    <i className="bi bi-plus" />
                  </button>
                  <button type="button" className="tbl-delete-btn"
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={e => { e.stopPropagation(); deleteColumn(hoverIndicator.index - 1, hoverIndicator.targetTable); setHoverIndicator(null); }}
                    title="Delete this column">
                    <i className="bi bi-dash" />
                  </button>
                </div>
              </>
            );
          }

          if (type === 'row') {
            const { rowRect } = hoverIndicator;
            const lineTop = rowRect.bottom - wr.top;
            const lineLeft = tableRect.left - wr.left;
            const lineWidth = tableRect.width;
            return (
              <>
                <div className="tbl-insert-line-h position-absolute" style={{ top: lineTop, left: lineLeft, width: lineWidth }} />
                <div className="tbl-btn-group position-absolute d-flex flex-column gap-1" style={{ top: lineTop - 22, left: lineLeft - 24 }}>
                  <button type="button" className="tbl-insert-btn"
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={e => { e.stopPropagation(); addRow(hoverIndicator.index, hoverIndicator.targetTable); setHoverIndicator(null); }}
                    title="Insert row here">
                    <i className="bi bi-plus" />
                  </button>
                  <button type="button" className="tbl-delete-btn"
                    disabled={hoverIndicator.index <= 0}
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={e => { e.stopPropagation(); const idx = hoverIndicator.index - 1; if (idx >= 0) deleteRow(idx, hoverIndicator.targetTable); setHoverIndicator(null); }}
                    title="Delete row above">
                    <i className="bi bi-dash" />
                  </button>
                </div>
              </>
            );
          }
          return null;
        })()}

        {/* Right-click context menu */}
        {isEditing && contextMenu && ReactDOM.createPortal(
          <>
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 2147483000 }}
              onClick={() => setContextMenu(null)} onWheel={() => setContextMenu(null)}
              onContextMenu={e => { e.preventDefault(); setContextMenu(null); }} />
            <div ref={positionContextMenu} className="tbl-context-menu ev-card-4 position-fixed"
              style={{ left: contextMenu.clientX, top: contextMenu.clientY, zIndex: 2147483001, visibility: 'hidden' }}>
              <button className="tbl-context-item" onClick={() => { addRow(contextMenu.rowIdx, contextMenu.targetTable); setContextMenu(null); }}>
                <i className="bi bi-arrow-up me-2" />Insert Row Above
              </button>
              <button className="tbl-context-item" onClick={() => { addRow(contextMenu.rowIdx + 1, contextMenu.targetTable); setContextMenu(null); }}>
                <i className="bi bi-arrow-down me-2" />Insert Row Below
              </button>
              <button className="tbl-context-item" onClick={() => { addColumn(contextMenu.colIdx, contextMenu.targetTable); setContextMenu(null); }}>
                <i className="bi bi-arrow-left me-2" />Insert Column Left
              </button>
              <button className="tbl-context-item" onClick={() => { addColumn(contextMenu.colIdx + 1, contextMenu.targetTable); setContextMenu(null); }}>
                <i className="bi bi-arrow-right me-2" />Insert Column Right
              </button>
              <div className="tbl-context-divider" />
              <button className="tbl-context-item text-danger" disabled={contextMenu.rowCount <= 1}
                onClick={() => {
                  const table = contextMenu.targetTable;
                  const row = contextMenu.targetRow;
                  if (!table || !row) { setContextMenu(null); return; }
                  const { map } = buildGridMap(table);
                  const allRows = getAllDirectRows(table);
                  const fullIdx = allRows.indexOf(row);
                  if (fullIdx >= 0 && fullIdx < map.length) {
                    for (const entry of map[fullIdx]) {
                      if (entry.cell === null && entry.coveredBy) {
                        const rs = parseInt(entry.coveredBy.getAttribute('rowspan') || '1', 10);
                        if (rs > 1) entry.coveredBy.setAttribute('rowspan', String(rs - 1));
                      } else if (entry.cell && entry.rs && entry.rs > 1) {
                        const nri = fullIdx + 1;
                        if (nri < allRows.length) {
                          const nextRow = allRows[nri];
                          entry.cell.setAttribute('rowspan', String(entry.rs - 1));
                          if (entry.rs - 1 <= 1) entry.cell.removeAttribute('rowspan');
                          let ins = false;
                          if (nri < map.length) {
                            for (const ne of map[nri]) {
                              if (ne.cell && ne.logStart >= entry.logStart) { nextRow.insertBefore(entry.cell, ne.cell); ins = true; break; }
                            }
                          }
                          if (!ins) nextRow.appendChild(entry.cell);
                        }
                      }
                    }
                  }
                  row.remove();
                  handleCellInput();
                  setContextMenu(null);
                }}>
                <i className="bi bi-trash me-2" />Delete Row
              </button>
              <button className="tbl-context-item text-danger" disabled={contextMenu.colCount <= 1}
                onClick={() => { deleteColumn(contextMenu.colIdx, contextMenu.targetTable); setContextMenu(null); }}>
                <i className="bi bi-trash me-2" />Delete Column
              </button>
            </div>
          </>,
          document.body
        )}
      </div>

      {isEditing && renderFormatPill && renderFormatPill({
        rect: pillRect,
        clipEl: tableRef.current,
        copyOnly: pillOnCitation,
        marks: pillMarks,
        cellState: pillCellState,
        onToggle: execInline,
        onColor: (c) => execInlineColour('foreColor', c),
        onHighlight: (c) => execInlineColour('hiliteColor', c),
        onCellBg: (c) => setCellProp('backgroundColor', c),
        onAlign: (a) => setCellProp('textAlign', a),
        onRemove: handleCellRemove,
        onMark: handleCellMark,
      })}

      {isEditing ? (
        <div
          ref={footerRef}
          className="genbi-table-footer-edit ev-fs-11 ev-small-p-dark mt-1"
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onFieldChange && onFieldChange('footnote', e.currentTarget.innerHTML)}
          onPaste={handleFooterPaste}
          onCopy={handlePillCopy}
          onClick={handlePillClick}
          data-placeholder="Table footnote (optional)..."
          dangerouslySetInnerHTML={{ __html: footer ?? '' }}
        />
      ) : (
        footer && (
          <div
            className="genbi-table-footer ev-fs-11 ev-small-p-dark mt-1"
            dangerouslySetInnerHTML={{ __html: footer }}
          />
        )
      )}
      {isEditing && (
        <MentionAutocomplete
          containerRef={tableRef}
          contentEditable
          docs={docs}
          refs={refs}
          onNeedReferences={onNeedReferences}
          onCreateReference={onCreateReference}
          refsLoading={refsLoading}
          onInsert={() => handleCellInput()}
        />
      )}
      {isEditing && (
        <MentionAutocomplete
          textareaRef={subtitleRef}
          docs={docs}
          refs={refs}
          onNeedReferences={onNeedReferences}
          onCreateReference={onCreateReference}
          refsLoading={refsLoading}
          onInsert={(newValue, newCaret) => {
            // Subtitle is plain text: swap the chip HTML for its [doc_id_N]/[ar_id_N]
            // token, which renderSubtitleWithCitations turns back into a badge.
            const toToken = (s) => s.replace(/<span[^>]*data-ref="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1');
            const value = toToken(newValue);
            const caret = toToken(newValue.slice(0, newCaret)).length;
            onFieldChange?.('subtitle', value);
            requestAnimationFrame(() => {
              const ta = subtitleRef.current;
              if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); }
            });
          }}
        />
      )}
      {isEditing && (
        <MentionAutocomplete
          containerRef={footerRef}
          contentEditable
          docs={docs}
          refs={refs}
          onNeedReferences={onNeedReferences}
          onCreateReference={onCreateReference}
          refsLoading={refsLoading}
          onInsert={() => {
            if (onFieldChange && footerRef.current) {
              onFieldChange('footnote', footerRef.current.innerHTML);
            }
          }}
        />
      )}
    </div>
  );
};

export default React.memo(TableComponent);